import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Search,
  User,
  Phone,
  Smartphone,
  Laptop,
  Tablet,
  Monitor,
  Clock,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Calendar,
  CheckCircle2,
  AlertCircle,
  FileText,
  DollarSign,
  Wallet,
  Check,
  CreditCard,
  Banknote,
  X,
  Sparkles,
  Building2,
  Truck,
  Layers,
  ArrowUpRight,
  ArrowDownLeft,
  Scale,
  Send,
  ExternalLink,
  Users,
  ListFilter,
  Tag,
  ArrowRight,
  Hash,
  Trash2,
} from 'lucide-react';
import { AppUser, PaymentMethod, ServiceTicket } from '../types';
import { calculateTicketTotal, formatDateTR, formatTL, formatPhoneDisplay, STATUS_COLORS, STATUS_LABELS, getStoredContractedCompanies, getTicketContractBreakdown, getStatusColors } from '../utils/storage';
import { ContractedCompaniesView } from './ContractedCompaniesView';

interface HistoryViewProps {
  tickets: ServiceTicket[];
  onSelectTicket: (ticket: ServiceTicket) => void;
  onUpdateTickets?: (updatedTickets: ServiceTicket[]) => void;
  isAdmin?: boolean;
  currentUser?: AppUser | null;
}

interface DeviceSummary {
  deviceType: string;
  deviceBrand: string;
  deviceModel: string;
  serialNumber?: string;
  ticketCount: number;
  lastTicketId: string;
  lastStatus: string;
  lastDate: string;
}

interface CustomerGroup {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  tickets: ServiceTicket[];
  devices: DeviceSummary[];
  totalDebt: number;
}

interface SubServiceGroup {
  name: string;
  tickets: ServiceTicket[];
  totalCost: number;
  paidCost: number;
  unpaidCost: number;
  deviceCount: number;
}

export const HistoryView: React.FC<HistoryViewProps> = ({
  tickets,
  onSelectTicket,
  onUpdateTickets,
  isAdmin = false,
  currentUser,
}) => {
  const [activeMainTab, setActiveMainTab] = useState<'customer' | 'subservice' | 'contracted'>('customer');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Customer Cari states
  const [selectedCustomerPhone, setSelectedCustomerPhone] = useState<string | null>(null);
  const [selectedDeviceKey, setSelectedDeviceKey] = useState<string | null>(null);

  // Sub-Service Cari states
  const [selectedSubServiceName, setSelectedSubServiceName] = useState<string | null>(null);
  const [subServiceFilter, setSubServiceFilter] = useState<'all' | 'unpaid' | 'paid'>('all');

  // Customer Debt Collection Modal States
  const [isCustomerCollectModalOpen, setIsCustomerCollectModalOpen] = useState(false);
  const [targetCustomer, setTargetCustomer] = useState<CustomerGroup | null>(null);
  const [singleTargetTicket, setSingleTargetTicket] = useState<ServiceTicket | null>(null);
  const [customerPaymentType, setCustomerPaymentType] = useState<'full' | 'partial'>('full');
  const [customerPayAmount, setCustomerPayAmount] = useState<number>(0);
  const [customerPaymentMethod, setCustomerPaymentMethod] = useState<PaymentMethod>('Nakit');
  const [customerCollectionNote, setCustomerCollectionNote] = useState('');

  // Customer Cari Deletion Modal States
  const [isDeleteCustomerModalOpen, setIsDeleteCustomerModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<CustomerGroup | null>(null);

  // Sub-Service Debt Payment Modal States (Only payment amount and optional note)
  const [isSubServicePayModalOpen, setIsSubServicePayModalOpen] = useState(false);
  const [targetSubServiceGroup, setTargetSubServiceGroup] = useState<SubServiceGroup | null>(null);
  const [singleSubServiceTicket, setSingleSubServiceTicket] = useState<ServiceTicket | null>(null);
  const [subServicePaymentType, setSubServicePaymentType] = useState<'full' | 'partial'>('full');
  const [subServicePayAmount, setSubServicePayAmount] = useState<number>(0);
  const [subServicePaymentNote, setSubServicePaymentNote] = useState('');

  // Toast Notification
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Click outside to close search dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Text highlight helper for search suggestions
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim() || !text) return text;
    const q = query.trim().toLowerCase();
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return text;

    const before = text.substring(0, idx);
    const match = text.substring(idx, idx + q.length);
    const after = text.substring(idx + q.length);

    return (
      <span>
        {before}
        <span className="bg-amber-200/90 text-amber-950 font-bold px-0.5 rounded">{match}</span>
        {after}
      </span>
    );
  };

  // --- CUSTOMER GROUPS CALCULATION ---
  const customerGroups = useMemo(() => {
    const map = new Map<string, CustomerGroup>();

    tickets.forEach(ticket => {
      const rawPhone = ticket.customerPhone ? ticket.customerPhone.replace(/\D/g, '') : '';
      const key = rawPhone || ticket.customerName.trim().toLowerCase();

      const breakdown = getTicketContractBreakdown(ticket);
      const debt = breakdown.effectiveDebt;

      if (!map.has(key)) {
        map.set(key, {
          name: ticket.customerName,
          phone: ticket.customerPhone,
          email: ticket.customerEmail,
          address: ticket.customerAddress,
          tickets: [ticket],
          devices: [],
          totalDebt: debt,
        });
      } else {
        const group = map.get(key)!;
        group.tickets.push(ticket);
        group.totalDebt += debt;
        if (!group.email && ticket.customerEmail) group.email = ticket.customerEmail;
        if (!group.address && ticket.customerAddress) group.address = ticket.customerAddress;
      }
    });

    // Calculate unique devices for each customer group
    map.forEach(group => {
      const devMap = new Map<string, DeviceSummary>();

      group.tickets.forEach(t => {
        const sNorm = (t.serialNumber || '').trim().toLowerCase();
        const hasValidSerial = sNorm && sNorm !== 'belirtilmedi' && sNorm !== 'yok' && sNorm !== '-';
        const dKey = hasValidSerial
          ? `sn:${sNorm}`
          : `${(t.deviceType || '').toLowerCase()}|${(t.deviceBrand || '').trim().toLowerCase()}|${(t.deviceModel || '').trim().toLowerCase()}`;

        if (!devMap.has(dKey)) {
          devMap.set(dKey, {
            deviceType: t.deviceType,
            deviceBrand: t.deviceBrand,
            deviceModel: t.deviceModel,
            serialNumber: hasValidSerial ? t.serialNumber : undefined,
            ticketCount: 1,
            lastTicketId: t.id,
            lastStatus: t.status,
            lastDate: t.createdAt,
          });
        } else {
          const dev = devMap.get(dKey)!;
          dev.ticketCount += 1;
          if (new Date(t.createdAt).getTime() > new Date(dev.lastDate).getTime()) {
            dev.lastTicketId = t.id;
            dev.lastStatus = t.status;
            dev.lastDate = t.createdAt;
          }
        }
      });

      group.devices = Array.from(devMap.values());
      group.tickets.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });

    return Array.from(map.values()).sort((a, b) => b.tickets.length - a.tickets.length);
  }, [tickets]);

  // --- SUB-SERVICE (ALT SERVİS) GROUPS CALCULATION ---
  const subServiceGroups = useMemo(() => {
    const map = new Map<string, SubServiceGroup>();

    tickets.forEach(ticket => {
      const name = (ticket.subServiceName || '').trim();
      const hasSubService = name.length > 0 || (ticket.subServiceCost !== undefined && ticket.subServiceCost > 0) || ticket.status === 'AltServiste';

      if (!hasSubService) return;

      const firmKey = name || 'Belirtilmemiş Alt Servis';
      const cost = ticket.subServiceCost || 0;
      const paid =
        ticket.subServicePaymentStatus === 'Odendi'
          ? (ticket.subServicePaidAmount !== undefined && ticket.subServicePaidAmount > 0 ? ticket.subServicePaidAmount : cost)
          : ticket.subServicePaymentStatus === 'KismiOdendi'
          ? (ticket.subServicePaidAmount || 0)
          : 0;
      const unpaid = Math.max(0, cost - paid);

      if (!map.has(firmKey)) {
        map.set(firmKey, {
          name: firmKey,
          tickets: [ticket],
          totalCost: cost,
          paidCost: paid,
          unpaidCost: unpaid,
          deviceCount: 1,
        });
      } else {
        const group = map.get(firmKey)!;
        group.tickets.push(ticket);
        group.totalCost += cost;
        group.paidCost += paid;
        group.unpaidCost += unpaid;
        group.deviceCount += 1;
      }
    });

    // Sort tickets inside groups
    map.forEach(g => {
      g.tickets.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    });

    return Array.from(map.values()).sort((a, b) => b.unpaidCost - a.unpaidCost || b.totalCost - a.totalCost);
  }, [tickets]);

  // --- OVERALL METRICS ---
  const totalCustomerReceivable = useMemo(() => {
    return customerGroups.reduce((sum, g) => sum + g.totalDebt, 0);
  }, [customerGroups]);

  const totalSubServicePayable = useMemo(() => {
    return subServiceGroups.reduce((sum, g) => sum + g.unpaidCost, 0);
  }, [subServiceGroups]);

  const totalSubServiceAllCost = useMemo(() => {
    return subServiceGroups.reduce((sum, g) => sum + g.totalCost, 0);
  }, [subServiceGroups]);

  const netCariBalance = totalCustomerReceivable - totalSubServicePayable;

  // --- FILTERED CUSTOMER GROUPS ---
  const filteredCustomerGroups = useMemo(() => {
    if (!searchQuery.trim()) return customerGroups;
    const q = searchQuery.toLowerCase();

    return customerGroups.filter(g => {
      const nameMatch = g.name.toLowerCase().includes(q);
      const phoneMatch = g.phone.includes(q);
      const deviceMatch = g.tickets.some(
        t =>
          t.deviceBrand.toLowerCase().includes(q) ||
          t.deviceModel.toLowerCase().includes(q) ||
          t.serialNumber.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q)
      );

      return nameMatch || phoneMatch || deviceMatch;
    });
  }, [customerGroups, searchQuery]);

  // --- FILTERED SUB-SERVICE GROUPS ---
  const filteredSubServiceGroups = useMemo(() => {
    let list = subServiceGroups;

    if (subServiceFilter === 'unpaid') {
      list = list.filter(g => g.unpaidCost > 0);
    } else if (subServiceFilter === 'paid') {
      list = list.filter(g => g.unpaidCost === 0 && g.totalCost > 0);
    }

    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();

    return list.filter(g => {
      const nameMatch = g.name.toLowerCase().includes(q);
      const ticketMatch = g.tickets.some(
        t =>
          t.customerName.toLowerCase().includes(q) ||
          t.customerPhone.includes(q) ||
          t.deviceBrand.toLowerCase().includes(q) ||
          t.deviceModel.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          (t.subServiceTrackingNo || '').toLowerCase().includes(q) ||
          (t.subServiceNotes || '').toLowerCase().includes(q)
      );
      return nameMatch || ticketMatch;
    });
  }, [subServiceGroups, subServiceFilter, searchQuery]);

  // --- SEARCH SUGGESTIONS & AUTOCOMPLETE COMPUTATION ---
  const searchSuggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const cleanPhoneQ = searchQuery.replace(/\D/g, '');

    if (!q) {
      // Empty input quick suggestions
      const debtors = customerGroups.filter(c => c.totalDebt > 0).slice(0, 3);
      const unpaidSubServices = subServiceGroups.filter(s => s.unpaidCost > 0).slice(0, 3);
      const recentCustomers = customerGroups.slice(0, 4);

      return {
        isEmpty: true,
        debtors,
        unpaidSubServices,
        recentCustomers,
        customerMatches: [] as CustomerGroup[],
        subServiceMatches: [] as SubServiceGroup[],
        deviceMatches: [] as Array<{
          key: string;
          customerName: string;
          customerPhone: string;
          deviceBrand: string;
          deviceModel: string;
          deviceType?: string;
          serialNumber?: string;
          ticketCount: number;
        }>,
        ticketMatches: [] as ServiceTicket[],
        totalMatches: debtors.length + unpaidSubServices.length + recentCustomers.length,
      };
    }

    // 1. Customer matches (name, phone, email)
    const customerMatches = customerGroups
      .filter(c => {
        const nameMatch = c.name.toLowerCase().includes(q);
        const phoneMatch = cleanPhoneQ.length >= 2 && c.phone && c.phone.replace(/\D/g, '').includes(cleanPhoneQ);
        const emailMatch = c.email && c.email.toLowerCase().includes(q);
        return nameMatch || phoneMatch || emailMatch;
      })
      .slice(0, 5);

    // 2. Sub-service matches (firm name)
    const subServiceMatches = subServiceGroups
      .filter(s => s.name.toLowerCase().includes(q))
      .slice(0, 4);

    // 3. Device & Serial / IMEI matches
    const deviceMatches: Array<{
      key: string;
      customerName: string;
      customerPhone: string;
      deviceBrand: string;
      deviceModel: string;
      deviceType?: string;
      serialNumber?: string;
      ticketCount: number;
    }> = [];

    const seenDeviceKeys = new Set<string>();
    customerGroups.forEach(c => {
      c.devices.forEach(d => {
        const brandMatch = d.deviceBrand.toLowerCase().includes(q);
        const modelMatch = d.deviceModel.toLowerCase().includes(q);
        const snMatch = d.serialNumber && d.serialNumber.toLowerCase().includes(q);

        if (brandMatch || modelMatch || snMatch) {
          const uniqueKey = `${c.phone || c.name}|${d.deviceBrand}|${d.deviceModel}|${d.serialNumber || ''}`;
          if (!seenDeviceKeys.has(uniqueKey)) {
            seenDeviceKeys.add(uniqueKey);
            deviceMatches.push({
              key: d.serialNumber
                ? `sn:${d.serialNumber.trim().toLowerCase()}`
                : `${(d.deviceType || '').toLowerCase()}|${d.deviceBrand.trim().toLowerCase()}|${d.deviceModel.trim().toLowerCase()}`,
              customerName: c.name,
              customerPhone: c.phone,
              deviceBrand: d.deviceBrand,
              deviceModel: d.deviceModel,
              deviceType: d.deviceType,
              serialNumber: d.serialNumber,
              ticketCount: d.ticketCount,
            });
          }
        }
      });
    });

    // 4. Ticket matches (ID, description, tracking no)
    const ticketMatches = tickets
      .filter(t => {
        const idMatch = t.id.toLowerCase().includes(q) || `#${t.id}`.toLowerCase().includes(q);
        const problemMatch = (t.problemDescription || '').toLowerCase().includes(q);
        const trackingMatch = (t.subServiceTrackingNo || '').toLowerCase().includes(q);
        return idMatch || problemMatch || trackingMatch;
      })
      .slice(0, 5);

    const totalMatches =
      customerMatches.length + subServiceMatches.length + deviceMatches.length + ticketMatches.length;

    return {
      isEmpty: false,
      debtors: [] as CustomerGroup[],
      unpaidSubServices: [] as SubServiceGroup[],
      recentCustomers: [] as CustomerGroup[],
      customerMatches,
      subServiceMatches,
      deviceMatches: deviceMatches.slice(0, 4),
      ticketMatches,
      totalMatches,
    };
  }, [searchQuery, customerGroups, subServiceGroups, tickets]);

  // Active Selected Customer
  const selectedCustomer = useMemo(() => {
    if (!selectedCustomerPhone) return filteredCustomerGroups[0] || customerGroups[0] || null;
    return customerGroups.find(g => g.phone === selectedCustomerPhone) || filteredCustomerGroups[0] || null;
  }, [selectedCustomerPhone, filteredCustomerGroups, customerGroups]);

  // Active Selected SubService
  const selectedSubService = useMemo(() => {
    if (!selectedSubServiceName) return filteredSubServiceGroups[0] || subServiceGroups[0] || null;
    return subServiceGroups.find(g => g.name === selectedSubServiceName) || filteredSubServiceGroups[0] || null;
  }, [selectedSubServiceName, filteredSubServiceGroups, subServiceGroups]);

  // Customer tickets for device filter
  const displayCustomerTickets = useMemo(() => {
    if (!selectedCustomer) return [];
    if (!selectedDeviceKey) return selectedCustomer.tickets;

    return selectedCustomer.tickets.filter(t => {
      const sNorm = (t.serialNumber || '').trim().toLowerCase();
      const hasValidSerial = sNorm && sNorm !== 'belirtilmedi' && sNorm !== 'yok' && sNorm !== '-';
      const dKey = hasValidSerial
        ? `sn:${sNorm}`
        : `${(t.deviceType || '').toLowerCase()}|${(t.deviceBrand || '').trim().toLowerCase()}|${(t.deviceModel || '').trim().toLowerCase()}`;

      return dKey === selectedDeviceKey;
    });
  }, [selectedCustomer, selectedDeviceKey]);

  // Helper for customer unpaid tickets
  const getCustomerUnpaidTickets = (group: CustomerGroup) => {
    return group.tickets.filter(t => {
      const breakdown = getTicketContractBreakdown(t);
      return breakdown.effectiveDebt > 0.01;
    });
  };

  // Helper for subservice unpaid tickets
  const getSubServiceUnpaidTickets = (group: SubServiceGroup) => {
    return group.tickets.filter(t => {
      const cost = t.subServiceCost || 0;
      if (cost <= 0) return false;
      const paid =
        t.subServicePaymentStatus === 'Odendi'
          ? (t.subServicePaidAmount !== undefined && t.subServicePaidAmount > 0 ? t.subServicePaidAmount : cost)
          : t.subServicePaymentStatus === 'KismiOdendi'
          ? (t.subServicePaidAmount || 0)
          : 0;
      return cost - paid > 0.01;
    });
  };

  // --- ACTIONS: CUSTOMER DEBT COLLECTION ---
  const handleOpenCustomerCollectModal = (group: CustomerGroup) => {
    setTargetCustomer(group);
    setSingleTargetTicket(null);
    setCustomerPaymentType('full');
    setCustomerPayAmount(group.totalDebt);
    setCustomerPaymentMethod('Nakit');
    setCustomerCollectionNote('');
    setIsCustomerCollectModalOpen(true);
  };

  const handleOpenSingleTicketCollectModal = (ticket: ServiceTicket, group: CustomerGroup) => {
    const breakdown = getTicketContractBreakdown(ticket);
    const ticketDebt = breakdown.effectiveDebt;

    setTargetCustomer(group);
    setSingleTargetTicket(ticket);
    setCustomerPaymentType('full');
    setCustomerPayAmount(ticketDebt);
    setCustomerPaymentMethod(ticket.paymentMethod || 'Nakit');
    setCustomerCollectionNote(breakdown.hasOutOfContractParts ? 'Sözleşme Harici Ekstra Parça Tahsilatı' : '');
    setIsCustomerCollectModalOpen(true);
  };

  const handleConfirmCustomerCollection = () => {
    if (!onUpdateTickets || !targetCustomer) return;

    const nowISO = new Date().toISOString();
    let updatedCount = 0;
    let totalCollected = 0;

    const enteredAmount = Math.max(0, customerPayAmount);
    if (enteredAmount <= 0) return;

    if (singleTargetTicket) {
      const breakdown = getTicketContractBreakdown(singleTargetTicket);
      const { grandTotal } = calculateTicketTotal(
        singleTargetTicket.parts,
        singleTargetTicket.laborCost,
        singleTargetTicket.vatRate,
        singleTargetTicket.discount,
        singleTargetTicket.isVatIncluded
      );

      const targetDebtTotal = singleTargetTicket.serviceType === 'Sozlesmeli'
        ? breakdown.outOfContractTotal
        : grandTotal;

      const prevPaid =
        singleTargetTicket.paymentStatus === 'Odendi'
          ? (singleTargetTicket.paidAmount !== undefined && singleTargetTicket.paidAmount > 0 ? singleTargetTicket.paidAmount : targetDebtTotal)
          : singleTargetTicket.paymentStatus === 'KismiOdendi'
          ? (singleTargetTicket.paidAmount || 0)
          : 0;

      const remainingDebt = Math.max(0, targetDebtTotal - prevPaid);
      const payingNow = customerPaymentType === 'full' ? remainingDebt : Math.min(enteredAmount, remainingDebt);
      const newTotalPaid = Math.min(targetDebtTotal, prevPaid + payingNow);
      const isFullyPaid = newTotalPaid >= targetDebtTotal - 0.01;

      totalCollected = payingNow;
      updatedCount = 1;

      const existingPayments = Array.isArray(singleTargetTicket.payments) ? [...singleTargetTicket.payments] : [];
      const paymentItem = {
        id: 'pay_' + Date.now(),
        amount: payingNow,
        method: customerPaymentMethod,
        date: nowISO,
        note: customerCollectionNote || (singleTargetTicket.serviceType === 'Sozlesmeli' ? 'Sözleşme Harici Ekstra Parça Tahsilatı' : 'Müşteri Cari Tahsilatı'),
      };

      const updatedTickets = tickets.map(t => {
        if (t.id === singleTargetTicket.id) {
          return {
            ...t,
            paymentStatus: isFullyPaid ? ('Odendi' as const) : ('KismiOdendi' as const),
            paidAmount: newTotalPaid,
            paymentMethod: customerPaymentMethod,
            paymentDate: nowISO,
            payments: [...existingPayments, paymentItem],
            updatedAt: nowISO,
          };
        }
        return t;
      });

      onUpdateTickets(updatedTickets);
      setIsCustomerCollectModalOpen(false);

      const msg = isFullyPaid
        ? `✅ ${singleTargetTicket.id} numaralı servis fişinin ${formatTL(totalCollected)} tutarındaki borcu tahsil edildi ve "Ödendi" olarak kaydedildi.`
        : `✅ ${singleTargetTicket.id} numaralı servis fişinden ${formatTL(totalCollected)} kısmi tahsilat yapıldı. (Kalan Borç: ${formatTL(targetDebtTotal - newTotalPaid)})`;

      setToastMessage(msg);
      setTimeout(() => setToastMessage(null), 5000);
      return;
    }

    // Toplu Müşteri Tahsilatı (Kısmi veya Tam)
    const rawTargetPhone = targetCustomer.phone.replace(/\D/g, '');
    const cleanTargetName = targetCustomer.name.trim().toLowerCase();

    // Müşteriye ait açık biletleri toplayalım
    const unpaidList = tickets.filter(t => {
      const rawPhone = t.customerPhone ? t.customerPhone.replace(/\D/g, '') : '';
      const cleanName = t.customerName.trim().toLowerCase();
      const isThisCust = (rawTargetPhone && rawPhone === rawTargetPhone) || cleanName === cleanTargetName;
      if (!isThisCust) return false;

      const breakdown = getTicketContractBreakdown(t);
      return breakdown.effectiveDebt > 0.01;
    });

    const totalCustDebt = unpaidList.reduce((sum, t) => {
      const breakdown = getTicketContractBreakdown(t);
      return sum + breakdown.effectiveDebt;
    }, 0);

    let remainingDistribute = customerPaymentType === 'full' ? totalCustDebt : Math.min(enteredAmount, totalCustDebt);
    totalCollected = remainingDistribute;

    const updatedTickets = tickets.map(t => {
      const rawPhone = t.customerPhone ? t.customerPhone.replace(/\D/g, '') : '';
      const cleanName = t.customerName.trim().toLowerCase();
      const isThisCust = (rawTargetPhone && rawPhone === rawTargetPhone) || cleanName === cleanTargetName;

      if (isThisCust && remainingDistribute > 0) {
        const breakdown = getTicketContractBreakdown(t);
        const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
        const targetDebtTotal = t.serviceType === 'Sozlesmeli' ? breakdown.outOfContractTotal : grandTotal;

        const prevPaid =
          t.paymentStatus === 'Odendi'
            ? (t.paidAmount !== undefined && t.paidAmount > 0 ? t.paidAmount : targetDebtTotal)
            : t.paymentStatus === 'KismiOdendi'
            ? (t.paidAmount || 0)
            : 0;

        const ticketDebt = Math.max(0, targetDebtTotal - prevPaid);
        if (ticketDebt > 0.01) {
          const payingForTicket = Math.min(remainingDistribute, ticketDebt);
          remainingDistribute -= payingForTicket;
          const newTotalPaid = Math.min(targetDebtTotal, prevPaid + payingForTicket);
          const isTicketFull = newTotalPaid >= targetDebtTotal - 0.01;

          updatedCount++;
          const existingPayments = Array.isArray(t.payments) ? [...t.payments] : [];
          const paymentItem = {
            id: 'pay_' + Date.now() + '_' + updatedCount,
            amount: payingForTicket,
            method: customerPaymentMethod,
            date: nowISO,
            note: customerCollectionNote || (t.serviceType === 'Sozlesmeli' ? 'Sözleşme Harici Ekstra Parça Tahsilatı' : 'Müşteri Cari Tahsilatı'),
          };

          return {
            ...t,
            paymentStatus: isTicketFull ? ('Odendi' as const) : ('KismiOdendi' as const),
            paidAmount: newTotalPaid,
            paymentMethod: customerPaymentMethod,
            paymentDate: nowISO,
            payments: [...existingPayments, paymentItem],
            updatedAt: nowISO,
          };
        }
      }
      return t;
    });

    onUpdateTickets(updatedTickets);
    setIsCustomerCollectModalOpen(false);

    const msg = `✅ ${targetCustomer.name} müşterisinden ${formatTL(totalCollected)} tahsil edildi (${updatedCount} adet servis formu güncellendi).`;
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 5000);
  };

  // --- ACTIONS: CUSTOMER CARI & TICKETS DELETION ---
  const handleOpenDeleteCustomerModal = (customer: CustomerGroup) => {
    if (!isAdmin) {
      alert('Yetkisiz İşlem: Müşteri ve cari kayıtlarını silme yetkisi sadece yönetici (admin) hesaplarında bulunmaktadır.');
      return;
    }
    setCustomerToDelete(customer);
    setIsDeleteCustomerModalOpen(true);
  };

  const handleConfirmDeleteCustomer = () => {
    if (!isAdmin) {
      alert('Yetkisiz İşlem: Müşteri ve cari kayıtlarını silme yetkisi sadece yönetici (admin) hesaplarında bulunmaktadır.');
      return;
    }
    if (!customerToDelete || !onUpdateTickets) return;

    const idsToDelete = new Set(customerToDelete.tickets.map(t => t.id));
    const nowISO = new Date().toISOString();
    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Sistem Yöneticisi';
    const currentUsername = currentUser?.username || 'admin';
    const updatedTickets = tickets.map(t =>
      idsToDelete.has(t.id)
        ? {
            ...t,
            isDeleted: true,
            deletedAt: nowISO,
            deletedByUser: currentUsername,
            deletedByName: currentUserName,
            updatedAt: nowISO,
          }
        : t
    );

    onUpdateTickets(updatedTickets);
    setIsDeleteCustomerModalOpen(false);

    // Reset customer selection if deleting current one
    if (selectedCustomer && (selectedCustomer.phone === customerToDelete.phone || selectedCustomer.name === customerToDelete.name)) {
      setSelectedCustomerPhone(null);
      setSelectedDeviceKey(null);
    }

    const msg = `🗑️ ${customerToDelete.name} müşterisine ait ${customerToDelete.tickets.length} adet servis kaydı sistemden gizlendi. Veritabanı kayıtları korundu.`;
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 5000);
  };

  // --- ACTIONS: SUB-SERVICE DEBT PAYMENT (No Payment Method - Amount & Note only) ---
  const handleOpenSubServiceGroupPayModal = (group: SubServiceGroup) => {
    setTargetSubServiceGroup(group);
    setSingleSubServiceTicket(null);
    setSubServicePaymentType('full');
    setSubServicePayAmount(group.unpaidCost);
    setSubServicePaymentNote('');
    setIsSubServicePayModalOpen(true);
  };

  const handleOpenSingleSubServicePayModal = (ticket: ServiceTicket, group: SubServiceGroup) => {
    const cost = ticket.subServiceCost || 0;
    const previouslyPaid =
      ticket.subServicePaymentStatus === 'Odendi'
        ? (ticket.subServicePaidAmount !== undefined && ticket.subServicePaidAmount > 0 ? ticket.subServicePaidAmount : cost)
        : ticket.subServicePaymentStatus === 'KismiOdendi'
        ? (ticket.subServicePaidAmount || 0)
        : 0;
    const debt = Math.max(0, cost - previouslyPaid);

    setTargetSubServiceGroup(group);
    setSingleSubServiceTicket(ticket);
    setSubServicePaymentType('full');
    setSubServicePayAmount(debt);
    setSubServicePaymentNote(ticket.subServicePaidNote || '');
    setIsSubServicePayModalOpen(true);
  };

  const handleConfirmSubServicePayment = () => {
    if (!onUpdateTickets || !targetSubServiceGroup) return;

    const nowISO = new Date().toISOString();
    let updatedCount = 0;
    let totalPaid = 0;

    const enteredAmount = Math.max(0, subServicePayAmount);
    if (enteredAmount <= 0) return;

    if (singleSubServiceTicket) {
      const cost = singleSubServiceTicket.subServiceCost || 0;
      const prevPaid =
        singleSubServiceTicket.subServicePaymentStatus === 'Odendi'
          ? (singleSubServiceTicket.subServicePaidAmount !== undefined && singleSubServiceTicket.subServicePaidAmount > 0 ? singleSubServiceTicket.subServicePaidAmount : cost)
          : singleSubServiceTicket.subServicePaymentStatus === 'KismiOdendi'
          ? (singleSubServiceTicket.subServicePaidAmount || 0)
          : 0;
      const remainingDebt = Math.max(0, cost - prevPaid);
      const payingNow = subServicePaymentType === 'full' ? remainingDebt : Math.min(enteredAmount, remainingDebt);
      const newTotalPaid = Math.min(cost, prevPaid + payingNow);
      const isFullyPaid = newTotalPaid >= cost - 0.01;

      totalPaid = payingNow;
      updatedCount = 1;

      let updatedNotes = singleSubServiceTicket.subServicePaidNote || '';
      if (subServicePaymentNote.trim()) {
        const entry = `[${new Date().toLocaleDateString('tr-TR')}] ${subServicePaymentNote.trim()} (${formatTL(payingNow)})`;
        updatedNotes = updatedNotes ? `${updatedNotes} | ${entry}` : entry;
      }

      const updatedTickets = tickets.map(t => {
        if (t.id === singleSubServiceTicket.id) {
          return {
            ...t,
            subServicePaymentStatus: isFullyPaid ? ('Odendi' as const) : ('KismiOdendi' as const),
            subServicePaidAmount: newTotalPaid,
            subServicePaidDate: nowISO,
            subServicePaidNote: updatedNotes || undefined,
            updatedAt: nowISO,
          };
        }
        return t;
      });

      onUpdateTickets(updatedTickets);
      setIsSubServicePayModalOpen(false);

      const msg = isFullyPaid
        ? `✅ #${singleSubServiceTicket.id} kaydının kalan ${formatTL(payingNow)} alt servis borcu tamamen ödendi ve borç kapatıldı.`
        : `✅ #${singleSubServiceTicket.id} kaydına ${formatTL(payingNow)} tutarında kısmi alt servis ödemesi işlendi. (Kalan Borç: ${formatTL(cost - newTotalPaid)})`;

      setToastMessage(msg);
      setTimeout(() => setToastMessage(null), 5000);
    } else {
      // Group Payment (Distribute amount across unpaid tickets FIFO)
      let remainingToDistribute = subServicePaymentType === 'full' ? targetSubServiceGroup.unpaidCost : enteredAmount;
      const targetFirmName = targetSubServiceGroup.name.trim().toLowerCase();

      const updatedTickets = tickets.map(t => {
        const firm = (t.subServiceName || 'Belirtilmemiş Alt Servis').trim().toLowerCase();
        if (firm !== targetFirmName) return t;

        const cost = t.subServiceCost || 0;
        if (cost <= 0) return t;

        const prevPaid =
          t.subServicePaymentStatus === 'Odendi'
            ? (t.subServicePaidAmount !== undefined && t.subServicePaidAmount > 0 ? t.subServicePaidAmount : cost)
            : t.subServicePaymentStatus === 'KismiOdendi'
            ? (t.subServicePaidAmount || 0)
            : 0;

        const ticketDebt = Math.max(0, cost - prevPaid);
        if (ticketDebt <= 0.01) return t;

        if (remainingToDistribute <= 0) return t;

        const payingForTicket = Math.min(remainingToDistribute, ticketDebt);
        const newTotalPaid = prevPaid + payingForTicket;
        const isTicketFull = newTotalPaid >= cost - 0.01;

        remainingToDistribute -= payingForTicket;
        totalPaid += payingForTicket;
        updatedCount++;

        let updatedNotes = t.subServicePaidNote || '';
        if (subServicePaymentNote.trim()) {
          const entry = `[${new Date().toLocaleDateString('tr-TR')}] ${subServicePaymentNote.trim()} (${formatTL(payingForTicket)})`;
          updatedNotes = updatedNotes ? `${updatedNotes} | ${entry}` : entry;
        }

        return {
          ...t,
          subServicePaymentStatus: isTicketFull ? ('Odendi' as const) : ('KismiOdendi' as const),
          subServicePaidAmount: newTotalPaid,
          subServicePaidDate: nowISO,
          subServicePaidNote: updatedNotes || undefined,
          updatedAt: nowISO,
        };
      });

      onUpdateTickets(updatedTickets);
      setIsSubServicePayModalOpen(false);

      const msg = `✅ "${targetSubServiceGroup.name}" firmasına ${formatTL(totalPaid)} tutarında alt servis ödemesi yapıldı (${updatedCount} adet cihaz güncellendi).`;
      setToastMessage(msg);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="bg-emerald-600 text-white px-5 py-3.5 rounded-2xl shadow-lg flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2.5 text-xs font-bold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200 shrink-0" />
            <span>{toastMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="p-1 hover:bg-emerald-700 rounded-lg text-emerald-100 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}


      {/* Top Header Card */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 text-blue-600 flex items-center justify-center font-bold">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                Müşteri ve Cariler Yönetim Merkezi
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Müşteri alacakları, cihaz arşivi ve alt servis (dış onarım) cari borç hesapları takibi
              </p>
            </div>
          </div>
        </div>

        {/* Search input with Smart Autocomplete / Suggestions */}
        <div ref={searchContainerRef} className="relative w-full md:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3 pointer-events-none" />
          <input
            type="text"
            placeholder="Müşteri, telefon, alt servis, IMEI veya fiş no..."
            value={searchQuery}
            onFocus={() => setIsSearchFocused(true)}
            onChange={e => {
              setSearchQuery(e.target.value);
              setIsSearchFocused(true);
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setIsSearchFocused(false);
              }
            }}
            className="w-full pl-10 pr-9 py-2 text-xs border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/60 focus:bg-white transition-all shadow-2xs"
          />

          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setIsSearchFocused(false);
              }}
              className="absolute right-2.5 top-2.5 p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100 cursor-pointer"
              title="Aramayı Temizle"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          {/* AUTOCOMPLETE / SUGGESTIONS DROPDOWN (Yalnızca yazmaya başlandığında açılır) */}
          {isSearchFocused && searchQuery.trim().length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-1">
              {searchSuggestions.totalMatches === 0 ? (
                // NO MATCHES FOUND
                <div className="p-6 text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                    <Search className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-bold text-slate-800">
                    "{searchQuery}" için sonuç bulunamadı
                  </p>
                  <p className="text-[11px] text-slate-400">
                    Müşteri adı, telefon numarası, alt servis, IMEI veya fiş no deneyebilirsiniz.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setIsSearchFocused(false);
                    }}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-bold underline cursor-pointer"
                  >
                    Aramayı Temizle
                  </button>
                </div>
              ) : (
                // MATCHES LIST GROUPED BY CATEGORY
                <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
                  {/* Category 1: Customers */}
                  {searchSuggestions.customerMatches.length > 0 && (
                    <div className="p-2.5 space-y-1">
                      <div className="flex items-center justify-between px-1.5 py-0.5 text-[10px] font-bold text-blue-700 uppercase tracking-wider">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span>Müşteriler ({searchSuggestions.customerMatches.length})</span>
                        </span>
                      </div>

                      {searchSuggestions.customerMatches.map(c => (
                        <button
                          key={c.phone || c.name}
                          type="button"
                          onClick={() => {
                            setSelectedCustomerPhone(c.phone || c.name);
                            setActiveMainTab('customer');
                            setSelectedDeviceKey(null);
                            setIsSearchFocused(false);
                          }}
                          className="w-full text-left p-2 rounded-xl hover:bg-blue-50/70 border border-transparent hover:border-blue-100 flex items-center justify-between transition-colors cursor-pointer group"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">
                              <User className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="text-xs font-bold text-slate-900 group-hover:text-blue-700 flex items-center gap-1.5">
                                <span>{highlightMatch(c.name, searchQuery)}</span>
                                {c.devices.length > 0 && (
                                  <span className="text-[10px] text-slate-400 font-normal">
                                    ({c.devices.length} Cihaz)
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-slate-400" />
                                  <span>{highlightMatch(formatPhoneDisplay(c.phone) || 'Telefon yok', searchQuery)}</span>
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            {c.totalDebt > 0 ? (
                              <div>
                                <span className="text-xs font-mono font-extrabold text-rose-600 block">
                                  {formatTL(c.totalDebt)}
                                </span>
                                <span className="text-[9px] text-rose-500 font-bold bg-rose-50 px-1.5 py-0.2 rounded border border-rose-200">
                                  Açık Borç
                                </span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md font-semibold border border-emerald-200">
                                Bakiye Kapalı
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Category 2: Sub-Services */}
                  {searchSuggestions.subServiceMatches.length > 0 && (
                    <div className="p-2.5 space-y-1">
                      <div className="flex items-center justify-between px-1.5 py-0.5 text-[10px] font-bold text-cyan-800 uppercase tracking-wider">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          <span>Alt Servis Firmaları ({searchSuggestions.subServiceMatches.length})</span>
                        </span>
                      </div>

                      {searchSuggestions.subServiceMatches.map(s => (
                        <button
                          key={s.name}
                          type="button"
                          onClick={() => {
                            setSelectedSubServiceName(s.name);
                            setActiveMainTab('subservice');
                            setIsSearchFocused(false);
                          }}
                          className="w-full text-left p-2 rounded-xl hover:bg-cyan-50/70 border border-transparent hover:border-cyan-100 flex items-center justify-between transition-colors cursor-pointer group"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-cyan-100 text-cyan-800 flex items-center justify-center font-bold text-xs shrink-0">
                              <Building2 className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-slate-900 group-hover:text-cyan-900">
                                {highlightMatch(s.name, searchQuery)}
                              </p>
                              <p className="text-[11px] text-slate-500">{s.deviceCount} Cihaz Kaydı</p>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            {s.unpaidCost > 0 ? (
                              <div>
                                <span className="text-xs font-mono font-extrabold text-rose-600 block">
                                  {formatTL(s.unpaidCost)}
                                </span>
                                <span className="text-[9px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.2 rounded border border-rose-200">
                                  Ödenecek
                                </span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-cyan-800 bg-cyan-50 px-1.5 py-0.5 rounded-md font-semibold border border-cyan-200">
                                Borç Yok
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Category 3: Registered Devices & Serial / IMEI */}
                  {searchSuggestions.deviceMatches.length > 0 && (
                    <div className="p-2.5 space-y-1">
                      <div className="flex items-center justify-between px-1.5 py-0.5 text-[10px] font-bold text-purple-800 uppercase tracking-wider">
                        <span className="flex items-center gap-1">
                          <Smartphone className="w-3 h-3" />
                          <span>Kayıtlı Cihazlar & IMEI ({searchSuggestions.deviceMatches.length})</span>
                        </span>
                      </div>

                      {searchSuggestions.deviceMatches.map(d => (
                        <button
                          key={d.key + d.customerPhone}
                          type="button"
                          onClick={() => {
                            setSelectedCustomerPhone(d.customerPhone || d.customerName);
                            setSelectedDeviceKey(d.key);
                            setActiveMainTab('customer');
                            setIsSearchFocused(false);
                          }}
                          className="w-full text-left p-2 rounded-xl hover:bg-purple-50/70 border border-transparent hover:border-purple-100 flex items-center justify-between transition-colors cursor-pointer group"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs shrink-0">
                              <Smartphone className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="text-xs font-bold text-slate-900 group-hover:text-purple-900 flex items-center gap-1.5">
                                <span>
                                  {highlightMatch(`${d.deviceBrand} ${d.deviceModel}`, searchQuery)}
                                </span>
                              </div>
                              <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                                <span>Sahibi: {highlightMatch(d.customerName, searchQuery)}</span>
                                {d.serialNumber && (
                                  <span className="font-mono text-[10px] bg-slate-100 px-1 py-0.2 rounded border border-slate-200 text-slate-700">
                                    SN: {highlightMatch(d.serialNumber, searchQuery)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            <span className="text-[11px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-lg border border-purple-200">
                              {d.ticketCount} Servis
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Category 4: Service Tickets */}
                  {searchSuggestions.ticketMatches.length > 0 && (
                    <div className="p-2.5 space-y-1">
                      <div className="flex items-center justify-between px-1.5 py-0.5 text-[10px] font-bold text-slate-700 uppercase tracking-wider">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          <span>Servis Fişleri ({searchSuggestions.ticketMatches.length})</span>
                        </span>
                      </div>

                      {searchSuggestions.ticketMatches.map(t => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            onSelectTicket(t);
                            setIsSearchFocused(false);
                          }}
                          className="w-full text-left p-2 rounded-xl hover:bg-slate-100/80 border border-transparent hover:border-slate-200 flex items-center justify-between transition-colors cursor-pointer group"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                              <Hash className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="text-xs font-bold text-slate-900 group-hover:text-blue-600 flex items-center gap-1.5">
                                <span className="font-mono text-blue-600">
                                  #{highlightMatch(t.id, searchQuery)}
                                </span>
                                <span>-</span>
                                <span>{highlightMatch(`${t.deviceBrand} ${t.deviceModel}`, searchQuery)}</span>
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                <span>{highlightMatch(t.customerName, searchQuery)}</span>
                                {t.subServiceTrackingNo && (
                                  <span className="ml-1.5 font-mono text-[10px] text-cyan-700">
                                    (Takip: {highlightMatch(t.subServiceTrackingNo, searchQuery)})
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            {(() => {
                              const sColors = getStatusColors(t.status);
                              return (
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-block border ${sColors.bg} ${sColors.text} ${sColors.border}`}
                                >
                                  {STATUS_LABELS[t.status as any] || t.status}
                                </span>
                              );
                            })()}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cari Finansal Genel Durum Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Müşteri Alacağı (Bize Ödenecek) */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs relative overflow-hidden">
          <div className="text-xs font-semibold text-slate-500">
            <span>Müşteri Alacakları</span>
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-mono font-extrabold text-emerald-600">
              {formatTL(totalCustomerReceivable)}
            </span>
            <span className="text-[11px] font-bold text-slate-400">
              {customerGroups.filter(g => g.totalDebt > 0).length} Borçlu Müşteri
            </span>
          </div>
        </div>

        {/* 2. Alt Servis Borcumuz (Bizim Ödeyeceğimiz) */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs relative overflow-hidden">
          <div className="text-xs font-semibold text-slate-500">
            <span>Alt Servis Borçları</span>
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-mono font-extrabold text-rose-600">
              {formatTL(totalSubServicePayable)}
            </span>
            <span className="text-[11px] font-bold text-slate-400">
              {subServiceGroups.filter(g => g.unpaidCost > 0).length} Açık Alt Servis
            </span>
          </div>
        </div>

        {/* 3. Net Cari Pozisyon */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs relative overflow-hidden">
          <div className="text-xs font-semibold text-slate-500">
            <span>Net Cari Pozisyon</span>
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className={`text-2xl font-mono font-extrabold ${
              netCariBalance >= 0 ? 'text-blue-600' : 'text-amber-600'
            }`}>
              {formatTL(netCariBalance)}
            </span>
            <span className="text-[11px] font-semibold text-slate-400">
              {netCariBalance >= 0 ? 'Pozitif Bakiye' : 'Negatif Bakiye'}
            </span>
          </div>
        </div>

        {/* 4. Toplam Kayıtlar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs relative overflow-hidden">
          <div className="text-xs font-semibold text-slate-500">
            <span>Kayıtlı Cari Sayısı</span>
          </div>
          <div className="mt-2 flex items-baseline justify-between gap-3">
            <span className="text-2xl font-mono font-extrabold text-slate-800">
              {customerGroups.length} <span className="text-sm font-normal text-slate-400">Müşteri</span>
            </span>
            <span className="text-2xl font-mono font-extrabold text-slate-800">
              {subServiceGroups.length} <span className="text-sm font-normal text-slate-400">Alt Servis</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Tab Switcher */}
      <div className="flex items-center space-x-2 border-b border-slate-200 pb-1">
        <button
          type="button"
          onClick={() => setActiveMainTab('customer')}
          className={`px-5 py-2.5 rounded-xl font-bold text-xs flex items-center space-x-2 transition-all cursor-pointer ${
            activeMainTab === 'customer'
              ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <User className="w-4 h-4" />
          <span>Müşteri Carileri & Cihaz Geçmişi</span>
          <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ml-1 ${
            activeMainTab === 'customer' ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700'
          }`}>
            {filteredCustomerGroups.length}
          </span>
          {totalCustomerReceivable > 0 && (
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ml-1 ${
              activeMainTab === 'customer' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-800'
            }`}>
              Alacak: {formatTL(totalCustomerReceivable)}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveMainTab('subservice')}
          className={`px-5 py-2.5 rounded-xl font-bold text-xs flex items-center space-x-2 transition-all cursor-pointer ${
            activeMainTab === 'subservice'
              ? 'bg-cyan-700 text-white shadow-sm shadow-cyan-700/20'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Alt Servis Carileri & Borç Takibi</span>
          <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ml-1 ${
            activeMainTab === 'subservice' ? 'bg-cyan-800 text-white' : 'bg-slate-100 text-slate-700'
          }`}>
            {subServiceGroups.length} Firma
          </span>
          {totalSubServicePayable > 0 && (
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ml-1 ${
              activeMainTab === 'subservice' ? 'bg-rose-500 text-white' : 'bg-rose-100 text-rose-800'
            }`}>
              Borcumuz: {formatTL(totalSubServicePayable)}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveMainTab('contracted')}
          className={`px-5 py-2.5 rounded-xl font-bold text-xs flex items-center space-x-2 transition-all cursor-pointer ${
            activeMainTab === 'contracted'
              ? 'bg-purple-700 text-white shadow-sm shadow-purple-700/20'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Building2 className="w-4 h-4 text-purple-400" />
          <span>Sözleşmeli Kurumsal Cariler</span>
          <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ml-1 ${
            activeMainTab === 'contracted' ? 'bg-purple-800 text-white' : 'bg-purple-100 text-purple-800'
          }`}>
            Ön Ödemeli / Bakiye
          </span>
        </button>
      </div>

      {/* ========================================================================= */}
      {/* SECTION 1: CUSTOMER CARİLERİ & CİHAZ GEÇMİŞİ */}
      {/* ========================================================================= */}
      {activeMainTab === 'customer' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Customer Cards List (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-4 space-y-3">
            <h3 className="font-bold text-xs text-slate-500 uppercase tracking-wider border-b pb-2 border-slate-100 flex items-center justify-between">
              <span>Müşteri Cari Listesi</span>
              <span className="text-blue-600 font-bold">{filteredCustomerGroups.length} Müşteri</span>
            </h3>

            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {filteredCustomerGroups.length > 0 ? (
                filteredCustomerGroups.map(group => {
                  const isSelected = selectedCustomer?.phone === group.phone;
                  return (
                    <div
                      key={group.phone || group.name}
                      onClick={() => setSelectedCustomerPhone(group.phone)}
                      className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                        isSelected
                          ? 'bg-blue-50 border-blue-300 shadow-sm'
                          : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/60'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                          {group.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="font-bold text-xs text-slate-900">{group.name}</h4>
                          <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {formatPhoneDisplay(group.phone) || 'Telefon Yok'}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="bg-blue-100 text-blue-900 text-[10px] px-2 py-0.5 rounded-full font-bold block mb-1">
                          {group.devices.length} Cihaz ({group.tickets.length} Kayıt)
                        </span>
                        {group.totalDebt > 0 ? (
                          <span className="font-mono text-xs font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-200 inline-block">
                            Borç: {formatTL(group.totalDebt)}
                          </span>
                        ) : (
                          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 inline-block">
                            Borç Yok
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-xs text-slate-400 italic text-center py-8">
                  Müşteri kaydı bulunamadı.
                </p>
              )}
            </div>
          </div>

          {/* Right Column: Selected Customer Timeline & History (7 cols) */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-6">
            {selectedCustomer ? (
              <>
                {/* Customer Header Profile with Debt Status & Collect Action */}
                <div className="bg-slate-900 text-white p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center font-bold text-lg text-white shrink-0">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{selectedCustomer.name}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300 mt-0.5">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5 text-blue-400" /> {formatPhoneDisplay(selectedCustomer.phone) || 'Telefon Belirtilmedi'}
                        </span>
                        {selectedCustomer.email && <span>• {selectedCustomer.email}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    {selectedCustomer.totalDebt > 0 ? (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="bg-rose-950/80 px-4 py-2 rounded-xl border border-rose-800 text-right">
                          <span className="text-[10px] text-rose-300 uppercase tracking-wider block font-semibold">
                            Kalan Toplam Borç
                          </span>
                          <span className="text-lg font-mono font-bold text-rose-400">
                            {formatTL(selectedCustomer.totalDebt)}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleOpenCustomerCollectModal(selectedCustomer)}
                          className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs flex items-center gap-2 shadow-lg shadow-emerald-950/40 transition-all cursor-pointer border border-emerald-500/50 hover:scale-[1.02] active:scale-[0.98]"
                          title="Müşterinin tüm ödenmemiş servis formlarını tahsil edildi olarak işaretle"
                        >
                          <Banknote className="w-4 h-4" />
                          <span>Müşteri Borcunu Tahsil Et</span>
                        </button>
                      </div>
                    ) : (
                      <div className="bg-emerald-950/80 px-4 py-2 rounded-xl border border-emerald-800 text-right flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <div>
                          <span className="text-[10px] text-emerald-300 uppercase tracking-wider block font-semibold">
                            Cari Durumu
                          </span>
                          <span className="text-xs font-bold text-emerald-300">
                            Borcu Yok
                          </span>
                        </div>
                      </div>
                    )}

                    {isAdmin && onUpdateTickets && (
                      <button
                        type="button"
                        onClick={() => handleOpenDeleteCustomerModal(selectedCustomer)}
                        className="px-3 py-2.5 bg-rose-900/60 hover:bg-rose-800 text-rose-200 hover:text-white rounded-xl font-bold text-xs flex items-center gap-1.5 border border-rose-700/60 transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                        title="Bu müşterinin tüm kayıtlarını ve cari hesabını temizle (Sadece Yönetici)"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-rose-300" />
                        <span>Müşteriyi Temizle</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Registered Devices List (Müşterinin Kayıtlı Cihazlar Listesi) */}
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                      <Smartphone className="w-4 h-4 text-blue-600" />
                      <span>Kayıtlı Cihazlar</span>
                      <span className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] px-2 py-0.5 rounded-full font-bold">
                        {selectedCustomer.devices.length} Cihaz
                      </span>
                    </h4>

                    {selectedDeviceKey && (
                      <button
                        type="button"
                        onClick={() => setSelectedDeviceKey(null)}
                        className="text-[11px] text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 bg-blue-50 hover:bg-blue-100/80 px-2.5 py-1 rounded-lg border border-blue-200 transition-colors cursor-pointer"
                      >
                        <ListFilter className="w-3 h-3" />
                        <span>Tüm Cihazların Fişlerini Göster</span>
                      </button>
                    )}
                  </div>

                  {/* Devices List Table */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                          <tr>
                            <th className="py-2.5 px-3">Cihaz & Model</th>
                            <th className="py-2.5 px-3">Seri No / IMEI</th>
                            <th className="py-2.5 px-3 text-center">Toplam Servis</th>
                            <th className="py-2.5 px-3">Son Durum & Tarih</th>
                            <th className="py-2.5 px-3 text-right">Filtrele</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {selectedCustomer.devices.map(d => {
                            const dKey = d.serialNumber
                              ? `sn:${d.serialNumber.trim().toLowerCase()}`
                              : `${d.deviceType.toLowerCase()}|${d.deviceBrand.trim().toLowerCase()}|${d.deviceModel.trim().toLowerCase()}`;
                            const isSel = selectedDeviceKey === dKey;

                            const devTypeLower = (d.deviceType || '').toLowerCase();
                            const DeviceIcon =
                              devTypeLower.includes('laptop') ||
                              devTypeLower.includes('pc') ||
                              devTypeLower.includes('bilgisayar') ||
                              devTypeLower.includes('notebook')
                                ? Laptop
                                : devTypeLower.includes('tablet') || devTypeLower.includes('ipad')
                                ? Tablet
                                : devTypeLower.includes('tv') ||
                                  devTypeLower.includes('monitör') ||
                                  devTypeLower.includes('ekran')
                                ? Monitor
                                : Smartphone;

                            return (
                              <tr
                                key={dKey}
                                onClick={() => setSelectedDeviceKey(isSel ? null : dKey)}
                                className={`transition-colors cursor-pointer ${
                                  isSel
                                    ? 'bg-blue-50/80 hover:bg-blue-100/60 font-medium'
                                    : 'hover:bg-slate-50/80'
                                }`}
                              >
                                <td className="py-2.5 px-3">
                                  <div className="flex items-center space-x-2.5">
                                    <div
                                      className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                        isSel ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                                      }`}
                                    >
                                      <DeviceIcon className="w-3.5 h-3.5" />
                                    </div>
                                    <div>
                                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                        <span>
                                          {d.deviceBrand} {d.deviceModel}
                                        </span>
                                        {isSel && (
                                          <span className="text-[9px] bg-blue-600 text-white px-1.5 py-0.2 rounded font-bold">
                                            Seçili
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-[11px] text-slate-400">{d.deviceType || 'Cihaz'}</span>
                                    </div>
                                  </div>
                                </td>

                                <td className="py-2.5 px-3">
                                  {d.serialNumber ? (
                                    <span className="font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 text-[11px]">
                                      {d.serialNumber}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400 text-[11px] italic">Belirtilmedi</span>
                                  )}
                                </td>

                                <td className="py-2.5 px-3 text-center">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                                    {d.ticketCount} Servis Kaydı
                                  </span>
                                </td>

                                <td className="py-2.5 px-3">
                                  <div className="space-y-0.5">
                                    {(() => {
                                      const sColors = getStatusColors(d.lastStatus);
                                      return (
                                        <span
                                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-block border ${sColors.bg} ${sColors.text} ${sColors.border}`}
                                        >
                                          {STATUS_LABELS[d.lastStatus as any] || d.lastStatus}
                                        </span>
                                      );
                                    })()}
                                    <div className="text-[10px] text-slate-400 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatDateTR(d.lastDate)}
                                    </div>
                                  </div>
                                </td>

                                <td className="py-2.5 px-3 text-right">
                                  <button
                                    type="button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setSelectedDeviceKey(isSel ? null : dKey);
                                    }}
                                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                                      isSel
                                        ? 'bg-blue-600 text-white shadow-2xs'
                                        : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
                                    }`}
                                  >
                                    {isSel ? 'Filtreli' : 'Fişleri Gör'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Service Tickets Timeline List */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                    <span>Servis İşlem Geçmişi & Formlar ({displayCustomerTickets.length})</span>
                  </h4>

                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                    {displayCustomerTickets.map(ticket => {
                      const breakdown = getTicketContractBreakdown(ticket);
                      const { grandTotal } = calculateTicketTotal(ticket.parts, ticket.laborCost, ticket.vatRate, ticket.discount, ticket.isVatIncluded, ticket.roundingAdjustment || 0);
                      const debt = breakdown.effectiveDebt;
                      const isContracted = ticket.serviceType === 'Sozlesmeli' || Boolean(ticket.contractedCompanyId);

                      return (
                        <div
                          key={ticket.id}
                          className="bg-slate-50/70 border border-slate-200 p-4 rounded-xl space-y-3 hover:border-slate-300 transition-colors"
                        >
                          {/* Ticket Header */}
                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-2">
                            <div className="flex items-center space-x-2">
                              <span className="font-mono text-xs font-extrabold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                {ticket.id}
                              </span>
                              {(() => {
                                const sColors = getStatusColors(ticket.status);
                                return (
                                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${sColors.bg} ${sColors.text} ${sColors.border}`}>
                                    {STATUS_LABELS[ticket.status]}
                                  </span>
                                );
                              })()}
                              {ticket.isUnderWarranty ? (
                                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 flex items-center gap-1">
                                  <ShieldCheck className="w-3 h-3" /> Garanti Kapsamında
                                </span>
                              ) : (
                                <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                  Garanti Dışı
                                </span>
                              )}
                              {ticket.subServiceName && (
                                <span className="text-[10px] font-medium text-cyan-800 bg-cyan-50 px-2 py-0.5 rounded border border-cyan-200 flex items-center gap-1">
                                  <Building2 className="w-3 h-3 text-cyan-600" />
                                  Alt Servis: {ticket.subServiceName}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center space-x-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                {formatDateTR(ticket.createdAt)}
                              </span>
                              <button
                                onClick={() => onSelectTicket(ticket)}
                                className="text-blue-600 hover:text-blue-700 font-bold flex items-center gap-0.5 hover:underline cursor-pointer"
                              >
                                Detay <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Device & Issue Specs */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-slate-400 font-medium block">Cihaz & Model:</span>
                              <span className="font-semibold text-slate-800">
                                {ticket.deviceBrand} {ticket.deviceModel} ({ticket.deviceType})
                              </span>
                            </div>

                            <div>
                              <span className="text-slate-400 font-medium block">Seri No / IMEI:</span>
                              <span className="font-mono text-slate-700">
                                {ticket.serialNumber || '-'}
                              </span>
                            </div>

                            <div className="sm:col-span-2">
                              <span className="text-slate-400 font-medium block">Müşteri Şikayeti / Arıza:</span>
                              <p className="text-slate-700 bg-white p-2 rounded-lg border border-slate-200/80 text-xs mt-0.5">
                                {ticket.issueDescription}
                              </p>
                            </div>

                            {ticket.technicianNotes && (
                              <div className="sm:col-span-2">
                                <span className="text-slate-400 font-medium block">Tekniker Notu & İşlemler:</span>
                                <p className="text-slate-700 bg-amber-50/50 p-2 rounded-lg border border-amber-200/60 text-xs mt-0.5 whitespace-pre-line">
                                  {ticket.technicianNotes}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Financial & Debt row */}
                          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200/80 bg-white p-2.5 rounded-lg">
                            <div className="flex items-center gap-3 text-xs">
                              <div>
                                <span className="text-slate-400 text-[11px] block">Toplam Tutar:</span>
                                <span className="font-mono font-bold text-slate-900">{formatTL(grandTotal)}</span>
                              </div>
                              <div className="border-l pl-3 border-slate-200">
                                <span className="text-slate-400 text-[11px] block">Ödeme Durumu:</span>
                                {isContracted ? (
                                  debt > 0 ? (
                                    <span className="font-mono font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-xs">
                                      Ekstra Parça Borcu: {formatTL(debt)}
                                    </span>
                                  ) : (
                                    <span className="font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 text-xs flex items-center gap-1">
                                      <Building2 className="w-3 h-3 text-purple-600" /> Sözleşme Kapsamında (Ödendi)
                                    </span>
                                  )
                                ) : debt > 0 ? (
                                  <span className="font-mono font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200 text-xs">
                                    Borç: {formatTL(debt)}
                                  </span>
                                ) : (
                                  <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 text-xs">
                                    Ödendi ({ticket.paymentMethod || 'Nakit'})
                                  </span>
                                )}
                              </div>
                            </div>

                            {debt > 0 && onUpdateTickets && (
                              <button
                                type="button"
                                onClick={() => handleOpenSingleTicketCollectModal(ticket, selectedCustomer)}
                                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-lg flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                              >
                                <Banknote className="w-3.5 h-3.5" />
                                <span>Bu Fişin Borcunu Tahsil Et ({formatTL(debt)})</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <User className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-semibold">Detaylarını görmek için soldan bir müşteri seçiniz.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION 2: ALT SERVİS (DIŞ ONARIM) CARİLERİ & BORÇ TAKİBİ */}
      {/* ========================================================================= */}
      {activeMainTab === 'subservice' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: SubService Firms List (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-2 border-slate-100">
              <h3 className="font-bold text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-cyan-600" />
                <span>Alt Servis Firmaları</span>
              </h3>
              <span className="text-cyan-700 font-bold text-xs">{filteredSubServiceGroups.length} Firma</span>
            </div>

            {/* Sub-Service Filter Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl text-xs">
              <button
                type="button"
                onClick={() => setSubServiceFilter('all')}
                className={`flex-1 py-1 text-center font-bold rounded-lg transition-all cursor-pointer ${
                  subServiceFilter === 'all' ? 'bg-white text-slate-800 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Tümü ({subServiceGroups.length})
              </button>
              <button
                type="button"
                onClick={() => setSubServiceFilter('unpaid')}
                className={`flex-1 py-1 text-center font-bold rounded-lg transition-all cursor-pointer ${
                  subServiceFilter === 'unpaid' ? 'bg-white text-rose-600 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Borçlu ({subServiceGroups.filter(g => g.unpaidCost > 0).length})
              </button>
              <button
                type="button"
                onClick={() => setSubServiceFilter('paid')}
                className={`flex-1 py-1 text-center font-bold rounded-lg transition-all cursor-pointer ${
                  subServiceFilter === 'paid' ? 'bg-white text-emerald-600 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Ödenmiş ({subServiceGroups.filter(g => g.unpaidCost === 0 && g.totalCost > 0).length})
              </button>
            </div>

            {/* List */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {filteredSubServiceGroups.length > 0 ? (
                filteredSubServiceGroups.map(group => {
                  const isSelected = selectedSubService?.name === group.name;
                  return (
                    <div
                      key={group.name}
                      onClick={() => setSelectedSubServiceName(group.name)}
                      className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                        isSelected
                          ? 'bg-cyan-50 border-cyan-300 shadow-sm ring-1 ring-cyan-200'
                          : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/60'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${
                          isSelected ? 'bg-cyan-700 text-white' : 'bg-slate-100 text-slate-700'
                        }`}>
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-bold text-xs text-slate-900">{group.name}</h4>
                          <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                            <Truck className="w-3 h-3 text-slate-400" />
                            {group.deviceCount} Cihaz Gönderildi
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-[10px] text-slate-400 block mb-0.5">
                          Toplam: {formatTL(group.totalCost)}
                        </span>
                        {group.unpaidCost > 0 ? (
                          <span className="font-mono text-xs font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-200 inline-block">
                            Borcumuz: {formatTL(group.unpaidCost)}
                          </span>
                        ) : (
                          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 inline-block">
                            Ödendi (Borç Yok)
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-xs text-slate-400 italic text-center py-8">
                  Kayıtlı alt servis firması veya eşleşen kayıt bulunamadı.
                </p>
              )}
            </div>
          </div>

          {/* Right Column: Selected Sub-Service Details & Tickets (7 cols) */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-6">
            {selectedSubService ? (
              <>
                {/* Firm Profile Header */}
                <div className="bg-slate-900 text-white p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-cyan-600 flex items-center justify-center font-bold text-lg text-white shrink-0">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{selectedSubService.name}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300 mt-0.5">
                        <span>Gönderilen Toplam Cihaz: <strong>{selectedSubService.deviceCount}</strong></span>
                        <span>•</span>
                        <span>Toplam Maliyet: <strong>{formatTL(selectedSubService.totalCost)}</strong></span>
                      </div>
                    </div>
                  </div>

                  {selectedSubService.unpaidCost > 0 ? (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="bg-rose-950/80 px-4 py-2 rounded-xl border border-rose-800 text-right">
                        <span className="text-[10px] text-rose-300 uppercase tracking-wider block font-semibold">
                          Açık Alt Servis Borcumuz
                        </span>
                        <span className="text-lg font-mono font-bold text-rose-400">
                          {formatTL(selectedSubService.unpaidCost)}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleOpenSubServiceGroupPayModal(selectedSubService)}
                        className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs flex items-center gap-2 shadow-lg shadow-emerald-950/40 transition-all cursor-pointer border border-emerald-500/50 hover:scale-[1.02] active:scale-[0.98]"
                        title="Bu firmaya ait tüm açık borçları ödendi olarak kapat"
                      >
                        <Banknote className="w-4 h-4" />
                        <span>Tüm Alt Servis Borcunu Öde & Kapat</span>
                      </button>
                    </div>
                  ) : (
                    <div className="bg-emerald-950/80 px-4 py-2 rounded-xl border border-emerald-800 text-right flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <div>
                        <span className="text-[10px] text-emerald-300 uppercase tracking-wider block font-semibold">
                          Alt Servis Cari Durumu
                        </span>
                        <span className="text-xs font-bold text-emerald-300">
                          Tüm Borçlar Ödendi (Borç Yok)
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sub-Service Device List */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                    <span>Bu Alt Servisteki Cihazlar & İşlem Formları ({selectedSubService.tickets.length})</span>
                    <span className="text-cyan-700 font-semibold text-xs">
                      {getSubServiceUnpaidTickets(selectedSubService).length} Cihazın Ücreti Ödenmedi
                    </span>
                  </h4>

                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                    {selectedSubService.tickets.map(ticket => {
                      const cost = ticket.subServiceCost || 0;
                      const paid =
                        ticket.subServicePaymentStatus === 'Odendi'
                          ? (ticket.subServicePaidAmount !== undefined && ticket.subServicePaidAmount > 0 ? ticket.subServicePaidAmount : cost)
                          : ticket.subServicePaymentStatus === 'KismiOdendi'
                          ? (ticket.subServicePaidAmount || 0)
                          : 0;
                      const unpaid = Math.max(0, cost - paid);
                      const isFullyPaid = unpaid <= 0.01 && cost > 0;
                      const isPartial = ticket.subServicePaymentStatus === 'KismiOdendi' || (paid > 0 && unpaid > 0.01);

                      return (
                        <div
                          key={ticket.id}
                          className={`p-4 rounded-xl border space-y-3 transition-colors ${
                            isFullyPaid
                              ? 'bg-slate-50/70 border-slate-200'
                              : isPartial
                              ? 'bg-amber-50/30 border-amber-200'
                              : 'bg-rose-50/30 border-rose-200'
                          }`}
                        >
                          {/* Ticket Header */}
                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-2">
                            <div className="flex items-center space-x-2">
                              <span className="font-mono text-xs font-extrabold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                {ticket.id}
                              </span>
                              {(() => {
                                const sColors = getStatusColors(ticket.status);
                                return (
                                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${sColors.bg} ${sColors.text} ${sColors.border}`}>
                                    {STATUS_LABELS[ticket.status]}
                                  </span>
                                );
                              })()}
                              {isFullyPaid ? (
                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                  Ödendi ({formatTL(cost)})
                                </span>
                              ) : isPartial ? (
                                <span className="text-[10px] font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 flex items-center gap-1">
                                  <Clock className="w-3 h-3 text-amber-600" />
                                  Kısmi Ödendi ({formatTL(paid)} / {formatTL(cost)})
                                </span>
                              ) : (
                                <span className="text-[10px] font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3 text-rose-600" />
                                  Ödenmedi - Cari Borç ({formatTL(cost)})
                                </span>
                              )}
                            </div>

                            <div className="flex items-center space-x-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                {formatDateTR(ticket.createdAt)}
                              </span>
                              <button
                                onClick={() => onSelectTicket(ticket)}
                                className="text-blue-600 hover:text-blue-700 font-bold flex items-center gap-0.5 hover:underline cursor-pointer"
                              >
                                Detay <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Customer & Device Information */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-slate-400 font-medium block">Müşteri Bilgisi:</span>
                              <span className="font-semibold text-slate-800">
                                {ticket.customerName} ({formatPhoneDisplay(ticket.customerPhone) || 'Telefon Yok'})
                              </span>
                            </div>

                            <div>
                              <span className="text-slate-400 font-medium block">Cihaz & Model:</span>
                              <span className="font-semibold text-slate-800">
                                {ticket.deviceBrand} {ticket.deviceModel}
                              </span>
                              {ticket.serialNumber && (
                                <span className="font-mono text-slate-500 text-[11px] block">
                                  SN: {ticket.serialNumber}
                                </span>
                              )}
                            </div>

                            {ticket.subServiceTrackingNo && (
                              <div>
                                <span className="text-slate-400 font-medium block">Kargo / Takip / Fiş No:</span>
                                <span className="font-mono font-bold text-cyan-800 bg-cyan-50 px-1.5 py-0.5 rounded border border-cyan-200">
                                  {ticket.subServiceTrackingNo}
                                </span>
                              </div>
                            )}

                            {ticket.subServiceNotes && (
                              <div className="sm:col-span-2">
                                <span className="text-slate-400 font-medium block">Alt Servis Notu / Yapılan İşlem:</span>
                                <p className="text-cyan-900 bg-cyan-50/60 p-2 rounded-lg border border-cyan-200/80 text-xs mt-0.5">
                                  {ticket.subServiceNotes}
                                </p>
                              </div>
                            )}

                            {ticket.subServicePaidNote && (
                              <div className="sm:col-span-2">
                                <span className="text-slate-400 font-medium block">Ödeme Açıklaması:</span>
                                <p className="text-emerald-900 bg-emerald-50/60 p-2 rounded-lg border border-emerald-200/80 text-xs mt-0.5">
                                  {ticket.subServicePaidNote}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Cost & Action Footer */}
                          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200/80 bg-white p-2.5 rounded-lg">
                            <div className="flex flex-wrap items-center gap-3 text-xs">
                              <div>
                                <span className="text-slate-400 text-[11px] block">Toplam Maliyet:</span>
                                <span className="font-mono font-extrabold text-slate-900 text-sm">
                                  {formatTL(cost)}
                                </span>
                              </div>

                              <div className="border-l pl-3 border-slate-200">
                                <span className="text-slate-400 text-[11px] block">Ödenen:</span>
                                <span className="font-mono font-bold text-emerald-700">
                                  {formatTL(paid)}
                                </span>
                              </div>

                              <div className="border-l pl-3 border-slate-200">
                                <span className="text-slate-400 text-[11px] block">Kalan Borç:</span>
                                {unpaid > 0.01 ? (
                                  <span className="font-bold text-rose-600 font-mono">
                                    {formatTL(unpaid)}
                                  </span>
                                ) : (
                                  <span className="font-bold text-emerald-600 flex items-center gap-1">
                                    <Check className="w-3.5 h-3.5" />
                                    Ödendi
                                  </span>
                                )}
                              </div>
                            </div>

                            {unpaid > 0.01 && cost > 0 && onUpdateTickets && (
                              <button
                                type="button"
                                onClick={() => handleOpenSingleSubServicePayModal(ticket, selectedSubService)}
                                className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-lg flex items-center gap-1.5 shadow-sm transition-all cursor-pointer hover:scale-[1.02]"
                              >
                                <Banknote className="w-3.5 h-3.5" />
                                <span>Ödeme Yap / Borç Kapat ({formatTL(unpaid)})</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-semibold">Detaylarını görmek için soldan bir alt servis firması seçiniz.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SECTION 3: SÖZLEŞMELİ KURUMSAL CARİLER & BAKİYE YÖNETİMİ */}
      {/* ========================================================================= */}
      {activeMainTab === 'contracted' && (
        <ContractedCompaniesView
          tickets={tickets}
          onSelectTicket={onSelectTicket}
          isAdmin={isAdmin}
          currentUser={currentUser}
        />
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: CUSTOMER DEBT COLLECTION (MÜŞTERİ TAHSİLAT MODALI - TAM & KISMİ) */}
      {/* ========================================================================= */}
      {isCustomerCollectModalOpen && targetCustomer && (() => {
        const totalTargetDebt = singleTargetTicket
          ? (() => {
              const { grandTotal } = calculateTicketTotal(
                singleTargetTicket.parts,
                singleTargetTicket.laborCost,
                singleTargetTicket.vatRate,
                singleTargetTicket.discount,
                singleTargetTicket.isVatIncluded
              );
              const paid =
                singleTargetTicket.paymentStatus === 'Odendi'
                  ? (singleTargetTicket.paidAmount !== undefined && singleTargetTicket.paidAmount > 0 ? singleTargetTicket.paidAmount : grandTotal)
                  : singleTargetTicket.paymentStatus === 'KismiOdendi'
                  ? singleTargetTicket.paidAmount || 0
                  : 0;
              return Math.max(0, grandTotal - paid);
            })()
          : targetCustomer.totalDebt;

        const effectiveCustomerPay =
          customerPaymentType === 'full'
            ? totalTargetDebt
            : Math.min(totalTargetDebt, Math.max(0, customerPayAmount));

        const customerRemainingAfterPay = Math.max(0, totalTargetDebt - effectiveCustomerPay);

        return (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm animate-in fade-in">
            <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 my-auto text-left">
              {/* Header */}
              <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center font-bold">
                    <Banknote className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-white">
                      {singleTargetTicket ? 'Servis Fişi Tahsilatı' : 'Müşteri Borç Tahsilatı'}
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Ödemeyi tam veya kısmi (ara ödeme) olarak alabilirsiniz.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsCustomerCollectModalOpen(false)}
                  className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                      Müşteri
                    </span>
                    <p className="text-sm font-bold text-slate-900">{targetCustomer.name}</p>
                    <p className="text-xs text-slate-500 font-mono">{formatPhoneDisplay(targetCustomer.phone)}</p>
                  </div>

                  <div className="text-right">
                    <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider block">
                      {singleTargetTicket ? 'Açık Fiş Borcu' : 'Toplam Müşteri Borcu'}
                    </span>
                    <span className="text-lg font-mono font-black text-rose-600">
                      {formatTL(totalTargetDebt)}
                    </span>
                  </div>
                </div>

                {/* Forms List */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                    <span>
                      {singleTargetTicket
                        ? 'İşlem Yapılacak Servis Fişi'
                        : `Açık Servis Formları (${getCustomerUnpaidTickets(targetCustomer).length} Form)`}
                    </span>
                    <span className="text-[11px] text-emerald-600 font-semibold">
                      {customerRemainingAfterPay === 0 ? 'Tümü "Ödendi" yapılacak' : 'Kısmi tahsilat işlenecek'}
                    </span>
                  </label>

                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                    {(singleTargetTicket ? [singleTargetTicket] : getCustomerUnpaidTickets(targetCustomer)).map(t => {
                      const breakdown = getTicketContractBreakdown(t);
                      const debt = breakdown.effectiveDebt;
                      const { grandTotal } = calculateTicketTotal(
                        t.parts,
                        t.laborCost,
                        t.vatRate,
                        t.discount,
                        t.isVatIncluded, t.roundingAdjustment || 0
                      );

                      return (
                        <div
                          key={t.id}
                          className="bg-white border border-slate-200 p-2.5 rounded-xl flex items-center justify-between text-xs shadow-2xs"
                        >
                          <div>
                            <span className="font-mono font-bold text-blue-600 mr-2">{t.id}</span>
                            <span className="font-semibold text-slate-800">
                              {t.deviceBrand} {t.deviceModel}
                            </span>
                            <span className="text-slate-400 text-[11px] ml-1.5">({formatDateTR(t.createdAt)})</span>
                          </div>
                          <div className="text-right">
                            <span className="font-mono font-bold text-rose-600">{formatTL(debt)}</span>
                            <span className="text-[10px] text-slate-400 block">Toplam: {formatTL(grandTotal)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Customer Payment Type (Full vs Partial) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800 block">
                    Tahsilat Şekli
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerPaymentType('full');
                        setCustomerPayAmount(totalTargetDebt);
                      }}
                      className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer font-bold text-xs flex flex-col items-center gap-1 ${
                        customerPaymentType === 'full'
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-500/20'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <span>Tam Tahsilat (Borcu Kapat)</span>
                      <span className="font-mono text-[11px] font-normal text-emerald-600">
                        {formatTL(totalTargetDebt)}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setCustomerPaymentType('partial');
                        if (customerPayAmount <= 0 || customerPayAmount >= totalTargetDebt) {
                          setCustomerPayAmount(Math.round(totalTargetDebt / 2));
                        }
                      }}
                      className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer font-bold text-xs flex flex-col items-center gap-1 ${
                        customerPaymentType === 'partial'
                          ? 'border-amber-600 bg-amber-50 text-amber-900 ring-2 ring-amber-500/20'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <span>Kısmi Tahsilat (Ara Ödeme)</span>
                      <span className="font-mono text-[11px] font-normal text-amber-700">
                        Özel Tutar Belirle
                      </span>
                    </button>
                  </div>
                </div>

                {/* Partial Payment Input & Chips */}
                {customerPaymentType === 'partial' && (
                  <div className="space-y-2 bg-amber-50/40 p-3 rounded-xl border border-amber-200/80 animate-in fade-in">
                    <label className="text-xs font-bold text-amber-950 flex items-center justify-between">
                      <span>Tahsil Edilecek Ara Tutar (₺)</span>
                      <span className="text-[11px] font-normal text-amber-800">
                        Maksimum: {formatTL(totalTargetDebt)}
                      </span>
                    </label>

                    <div className="relative">
                      <input
                        type="number"
                        step="any"
                        min="1"
                        max={totalTargetDebt}
                        value={customerPayAmount || ''}
                        onChange={e => {
                          const val = parseFloat(e.target.value) || 0;
                          setCustomerPayAmount(val);
                        }}
                        placeholder="0"
                        className="w-full pl-3 pr-10 py-2.5 bg-white border border-amber-300 rounded-xl text-sm font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                      />
                      <span className="absolute right-3.5 top-2.5 font-bold text-xs text-slate-400">₺</span>
                    </div>

                    {/* Quick Chips */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {[100, 250, 500, 1000].filter(a => a < totalTargetDebt).map(amt => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setCustomerPayAmount(amt)}
                          className="px-2.5 py-1 text-[11px] font-mono font-semibold bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100/60 transition-colors cursor-pointer"
                        >
                          {formatTL(amt)}
                        </button>
                      ))}

                      {totalTargetDebt > 50 && (
                        <button
                          type="button"
                          onClick={() => setCustomerPayAmount(Math.round(totalTargetDebt / 2))}
                          className="px-2.5 py-1 text-[11px] font-semibold bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100/60 transition-colors cursor-pointer"
                        >
                          %50 ({formatTL(Math.round(totalTargetDebt / 2))})
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setCustomerPayAmount(totalTargetDebt)}
                        className="px-2.5 py-1 text-[11px] font-semibold bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100/60 transition-colors cursor-pointer"
                      >
                        Tamamı ({formatTL(totalTargetDebt)})
                      </button>
                    </div>

                    {/* Live Calculation Feedback */}
                    <div className="pt-2 border-t border-amber-200/60 flex items-center justify-between text-xs font-semibold">
                      <span className="text-slate-600">Tahsilat Sonrası Kalan Müşteri Borcu:</span>
                      <span className={`font-mono font-bold ${customerRemainingAfterPay > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {formatTL(customerRemainingAfterPay)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Payment Method Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-800 block">
                    Tahsilat / Ödeme Yöntemi
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'Nakit' as PaymentMethod, label: 'Nakit', icon: Banknote },
                      { key: 'Kredi Karti' as PaymentMethod, label: 'Kredi Kartı', icon: CreditCard },
                      { key: 'EFT/Havale' as PaymentMethod, label: 'EFT / Havale', icon: Wallet },
                    ].map(m => {
                      const isSel = customerPaymentMethod === m.key;
                      const Icon = m.icon;
                      return (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setCustomerPaymentMethod(m.key)}
                          className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer flex flex-col items-center gap-1.5 ${
                            isSel
                              ? 'border-emerald-600 bg-emerald-50/60 ring-2 ring-emerald-500/20 text-emerald-800'
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${isSel ? 'text-emerald-600' : 'text-slate-500'}`} />
                          <span className="text-xs font-bold">{m.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Collection Note */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800 block">
                    Tahsilat Notu / Açıklama
                  </label>
                  <input
                    type="text"
                    value={customerCollectionNote}
                    onChange={e => setCustomerCollectionNote(e.target.value)}
                    placeholder="Tahsilat notu yazın..."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 p-4 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsCustomerCollectModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-xl transition-colors cursor-pointer"
                >
                  Vazgeç
                </button>

                <button
                  type="button"
                  onClick={handleConfirmCustomerCollection}
                  disabled={effectiveCustomerPay <= 0}
                  className={`px-5 py-2.5 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-md transition-all cursor-pointer ${
                    effectiveCustomerPay <= 0
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/20'
                  }`}
                >
                  <Check className="w-4 h-4" />
                  <span>
                    {customerPaymentType === 'full'
                      ? singleTargetTicket
                        ? `Fiş Borcunu Tahsil Et (${formatTL(effectiveCustomerPay)})`
                        : `Tüm Borcu Tahsil Et (${formatTL(effectiveCustomerPay)})`
                      : `Kısmi Tahsilatı Onayla (${formatTL(effectiveCustomerPay)})`}
                  </span>
                </button>
              </div>
            </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================================= */}
      {/* MODAL 2: SUB-SERVICE DEBT PAYMENT (ALT SERVİS BORÇ ÖDEME MODALI - KART/NAKİTSİZ) */}
      {/* ========================================================================= */}
      {isSubServicePayModalOpen && targetSubServiceGroup && (() => {
        const modalTargetCost = singleSubServiceTicket
          ? singleSubServiceTicket.subServiceCost || 0
          : targetSubServiceGroup.totalCost;

        const modalPaidSoFar = singleSubServiceTicket
          ? singleSubServiceTicket.subServicePaymentStatus === 'Odendi'
            ? singleSubServiceTicket.subServicePaidAmount || singleSubServiceTicket.subServiceCost || 0
            : singleSubServiceTicket.subServicePaidAmount || 0
          : targetSubServiceGroup.paidCost;

        const modalRemainingDebt = singleSubServiceTicket
          ? Math.max(0, modalTargetCost - modalPaidSoFar)
          : targetSubServiceGroup.unpaidCost;

        const effectivePayNow =
          subServicePaymentType === 'full'
            ? modalRemainingDebt
            : Math.min(modalRemainingDebt, Math.max(0, subServicePayAmount));

        const remainingAfterThisPay = Math.max(0, modalRemainingDebt - effectivePayNow);

        const targetUnpaidTickets = singleSubServiceTicket
          ? [singleSubServiceTicket]
          : getSubServiceUnpaidTickets(targetSubServiceGroup);

        return (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm animate-in fade-in">
            <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 my-auto text-left">
                {/* Header */}
                <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <div className="w-9 h-9 rounded-xl bg-cyan-600 flex items-center justify-center font-bold">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-white">
                      {singleSubServiceTicket ? `Alt Servis Borç Ödemesi (#${singleSubServiceTicket.id})` : `Alt Servis Borç Ödemesi (${targetSubServiceGroup.name})`}
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Ödemeyi tam veya kısmi (ödenen miktar) olarak işleyebilirsiniz.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsSubServicePayModalOpen(false)}
                  className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                {/* Financial Summary */}
                <div className="bg-cyan-50/70 border border-cyan-200 p-3.5 rounded-xl">
                  <div className="flex items-center justify-between border-b border-cyan-200/60 pb-2 mb-2">
                    <div>
                      <span className="text-[10px] font-bold text-cyan-800 uppercase tracking-wider block">
                        Alt Servis Firma
                      </span>
                      <p className="text-sm font-bold text-cyan-950">{targetSubServiceGroup.name}</p>
                    </div>
                    <span className="text-xs font-bold text-cyan-700 bg-white/80 px-2 py-0.5 rounded-md border border-cyan-200">
                      {singleSubServiceTicket ? 'Tek Cihaz Kaydı' : `${targetUnpaidTickets.length} Cihaz`}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-white/80 p-2 rounded-lg border border-cyan-100">
                      <span className="text-[10px] text-slate-500 block">Toplam Maliyet</span>
                      <span className="font-mono font-bold text-slate-800">{formatTL(modalTargetCost)}</span>
                    </div>
                    <div className="bg-white/80 p-2 rounded-lg border border-cyan-100">
                      <span className="text-[10px] text-slate-500 block">Önceki Ödenen</span>
                      <span className="font-mono font-bold text-emerald-700">{formatTL(modalPaidSoFar)}</span>
                    </div>
                    <div className="bg-rose-50 p-2 rounded-lg border border-rose-200">
                      <span className="text-[10px] text-rose-700 font-bold block">Açık Borç</span>
                      <span className="font-mono font-extrabold text-rose-600">{formatTL(modalRemainingDebt)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Type Selection (Full vs Partial) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800 block">
                    Ödeme Şekli
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSubServicePaymentType('full');
                        setSubServicePayAmount(modalRemainingDebt);
                      }}
                      className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer font-bold text-xs flex flex-col items-center gap-1 ${
                        subServicePaymentType === 'full'
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-500/20'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <span>Tam Ödeme (Borcu Kapat)</span>
                      <span className="font-mono text-[11px] font-normal text-emerald-600">
                        {formatTL(modalRemainingDebt)}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setSubServicePaymentType('partial');
                        if (subServicePayAmount <= 0 || subServicePayAmount >= modalRemainingDebt) {
                          setSubServicePayAmount(Math.round(modalRemainingDebt / 2));
                        }
                      }}
                      className={`p-2.5 rounded-xl border text-center transition-all cursor-pointer font-bold text-xs flex flex-col items-center gap-1 ${
                        subServicePaymentType === 'partial'
                          ? 'border-amber-600 bg-amber-50 text-amber-900 ring-2 ring-amber-500/20'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <span>Kısmi Ödeme (Ara Ödeme)</span>
                      <span className="font-mono text-[11px] font-normal text-amber-700">
                        Özel Tutar Belirle
                      </span>
                    </button>
                  </div>
                </div>

                {/* Partial Payment Amount Input & Quick Chips */}
                {subServicePaymentType === 'partial' && (
                  <div className="space-y-2 bg-amber-50/40 p-3 rounded-xl border border-amber-200/80 animate-in fade-in">
                    <label className="text-xs font-bold text-amber-950 flex items-center justify-between">
                      <span>Ödenecek Ara Tutar (₺)</span>
                      <span className="text-[11px] font-normal text-amber-800">
                        Maksimum: {formatTL(modalRemainingDebt)}
                      </span>
                    </label>

                    <div className="relative">
                      <input
                        type="number"
                        step="any"
                        min="1"
                        max={modalRemainingDebt}
                        value={subServicePayAmount || ''}
                        onChange={e => {
                          const val = parseFloat(e.target.value) || 0;
                          setSubServicePayAmount(val);
                        }}
                        placeholder="0"
                        className="w-full pl-3 pr-10 py-2.5 bg-white border border-amber-300 rounded-xl text-sm font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                      />
                      <span className="absolute right-3.5 top-2.5 font-bold text-xs text-slate-400">₺</span>
                    </div>

                    {/* Quick Chips */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {[100, 250, 500, 1000].filter(a => a < modalRemainingDebt).map(amt => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setSubServicePayAmount(amt)}
                          className="px-2.5 py-1 text-[11px] font-mono font-semibold bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100/60 transition-colors cursor-pointer"
                        >
                          {formatTL(amt)}
                        </button>
                      ))}

                      {modalRemainingDebt > 50 && (
                        <button
                          type="button"
                          onClick={() => setSubServicePayAmount(Math.round(modalRemainingDebt / 2))}
                          className="px-2.5 py-1 text-[11px] font-semibold bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100/60 transition-colors cursor-pointer"
                        >
                          %50 ({formatTL(Math.round(modalRemainingDebt / 2))})
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setSubServicePayAmount(modalRemainingDebt)}
                        className="px-2.5 py-1 text-[11px] font-semibold bg-white border border-amber-200 text-amber-900 rounded-lg hover:bg-amber-100/60 transition-colors cursor-pointer"
                      >
                        Tamamı ({formatTL(modalRemainingDebt)})
                      </button>
                    </div>

                    {/* Live Calculation Feedback */}
                    <div className="pt-2 border-t border-amber-200/60 flex items-center justify-between text-xs font-semibold">
                      <span className="text-slate-600">Ödeme Sonrası Kalan Borç:</span>
                      <span className={`font-mono font-bold ${remainingAfterThisPay > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {formatTL(remainingAfterThisPay)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Tickets List Preview */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800 flex items-center justify-between">
                    <span>
                      {singleSubServiceTicket
                        ? 'İşlem Yapılacak Servis Fişi'
                        : `Etkilenen Kayıtlar (${targetUnpaidTickets.length} Cihaz)`}
                    </span>
                    <span className="text-[11px] text-slate-500 font-normal">
                      {remainingAfterThisPay === 0 ? 'Tümü "Ödendi" yapılacak' : 'Kısmi ödeme dağıtılacak'}
                    </span>
                  </label>

                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                    {targetUnpaidTickets.map(t => {
                      const cost = t.subServiceCost || 0;
                      const paid =
                        t.subServicePaymentStatus === 'Odendi'
                          ? (t.subServicePaidAmount !== undefined && t.subServicePaidAmount > 0 ? t.subServicePaidAmount : cost)
                          : t.subServicePaymentStatus === 'KismiOdendi'
                          ? (t.subServicePaidAmount || 0)
                          : 0;
                      const unpaid = Math.max(0, cost - paid);

                      return (
                        <div
                          key={t.id}
                          className="bg-white border border-slate-200 p-2 rounded-xl flex items-center justify-between text-xs shadow-2xs"
                        >
                          <div>
                            <span className="font-mono font-bold text-blue-600 mr-1.5">{t.id}</span>
                            <span className="font-semibold text-slate-800">
                              {t.deviceBrand} {t.deviceModel}
                            </span>
                            <span className="text-slate-400 text-[11px] ml-1">({t.customerName})</span>
                          </div>
                          <div className="text-right">
                            <span className="font-mono font-bold text-rose-600">{formatTL(unpaid)}</span>
                            {paid > 0 && (
                              <span className="text-[10px] text-slate-400 block">Ödenen: {formatTL(paid)}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Payment Note */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-800 block">
                    Ödeme Açıklaması / Notu <span className="text-slate-400 font-normal">(İsteğe bağlı)</span>
                  </label>
                  <input
                    type="text"
                    value={subServicePaymentNote}
                    onChange={e => setSubServicePaymentNote(e.target.value)}
                    placeholder="Örn: Dekont no veya ödeme notu..."
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 p-4 border-t border-slate-200 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsSubServicePayModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-xl transition-colors cursor-pointer"
                >
                  Vazgeç
                </button>

                <button
                  type="button"
                  onClick={handleConfirmSubServicePayment}
                  disabled={effectivePayNow <= 0}
                  className={`px-5 py-2.5 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-md transition-all cursor-pointer ${
                    effectivePayNow <= 0
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/20'
                  }`}
                >
                  <Check className="w-4 h-4" />
                  <span>
                    {subServicePaymentType === 'full'
                      ? `Tam Ödemeyi Onayla (${formatTL(effectivePayNow)})`
                      : `Kısmi Ödemeyi Onayla (${formatTL(effectivePayNow)})`}
                  </span>
                </button>
              </div>
            </div>
            </div>
          </div>
        );
      })()}

      {/* CUSTOMER CARI & TICKETS DELETION MODAL */}
      {isDeleteCustomerModalOpen && customerToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-rose-200 animate-in fade-in zoom-in-95 duration-150">
            <div className="p-5 bg-rose-50 border-b border-rose-100 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-rose-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-slate-900">Müşteri Kaydını Temizle</h3>
                <p className="text-xs text-rose-700 mt-0.5 font-medium">
                  {customerToDelete.name}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDeleteCustomerModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-white/60 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs text-slate-600">
              <p>
                <strong className="text-slate-900">{customerToDelete.name}</strong> müşterisine ait toplam{' '}
                <strong className="text-rose-600 font-bold">{customerToDelete.tickets.length} adet</strong> servis formu ve cari hareketleri kalıcı olarak sistemden silinecektir.
              </p>

              {customerToDelete.totalDebt > 0 && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <span>
                    Dikkat: Bu müşterinin henüz tahsil edilmemiş <strong className="font-mono font-bold text-amber-900">{formatTL(customerToDelete.totalDebt)}</strong> tutarında borcu bulunmaktadır.
                  </span>
                </div>
              )}

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <span className="font-bold text-slate-700 block mb-1.5">Sistemden Gizlenecek Servis Formları:</span>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {customerToDelete.tickets.map(t => (
                    <span key={t.id} className="font-mono text-[11px] font-bold bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-700">
                      {t.id}
                    </span>
                  ))}
                </div>
              </div>

              <p className="text-slate-500 italic">
                Bu işlem geri alınamaz. Devam etmek istiyor musunuz?
              </p>
            </div>

            <div className="bg-slate-50 px-5 py-3.5 border-t border-slate-200 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setIsDeleteCustomerModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-xl transition-colors cursor-pointer"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteCustomer}
                className="px-4 py-2 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl shadow-md shadow-rose-600/20 flex items-center gap-1.5 transition-all cursor-pointer"
              >
                <Trash2 className="w-4 h-4" />
                <span>Müşteriyi ve Formları Gizle</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
